import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { CrossIcon, PlusIcon, PlusSignIcon } from "../../assets/icons";
import { IDialogProps } from "../../openapi/interfaces";
import Transition from "../Transition";
import Api from "../../openapi";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import { usePlans, usePlanOptions, usePlan } from "../../hooks/usePlans";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import useSubscriptionValidation from "./SubscriptionValidation";
import {
  AdminPlanDetailResponse,
  AdminPlanDetailResponseAvailableDaysEnum,
  AdminSubscriptionDetailResponse,
  CreateSubscriptionRequest,
  PartnerUserDetailResponse,
  PlanDetailPlanOptionResponseMealTypeListEnum,
  PriceOfferDiscountResponseStatusEnum,
  SubscriptionDetailResponse,
  UpdateSubscriptionRequest,
} from "../../openapi/client";
import { checkEmpty, checkIf, DayList, debounce, defaultFormat, displayFormat, formatCurrency, generateRandom } from "../../utils";
import useZones from "../../hooks/useZones";
import { RootState } from "../../store";
import useMealTypes from "../../hooks/useMealTypes";
import useLockedDays from "../../hooks/useLockedDays";
import useDiscount from "../../hooks/useDiscount";
import { IAddressResponse } from "../../pages/UserDetail";
import usePriceOffer from "../../hooks/usePriceOffer";
import DiscountCrud from "../DiscountCrud";
import { useDebounce } from "../../hooks/useDebounce";

interface ISubscriptionCrud extends IDialogProps {
  userId?: string;
  plan?: AdminSubscriptionDetailResponse;
  addressList?: IAddressResponse[];
}

export default function SubscriptionCrud(props: ISubscriptionCrud) {
  const { lockedStart } = useLockedDays();
  const [formDirty, setFormDirty] = useState(false);
  const [subscription, setSubscriptionEdit] = useState<any>({
    userId: props.userId,
    planId: props.plan?.planOption?.plan?.id || "",
    planOptionId: props.plan?.planOption?.id || "",
    discountCode: props.plan?.discountCode,
    startDate: props.plan?.startDate ? moment(props.plan?.startDate) : moment(lockedStart),
    assignedDays: props.plan?.assignedDays || "",
    deliveryDays: props.plan?.deliveryDays,
    dietaryTargets: {
      calories: props.plan?.dietaryTargets?.calories,
      carb: props.plan?.dietaryTargets?.carb,
      fat: props.plan?.dietaryTargets?.fat,
      protein: props.plan?.dietaryTargets?.protein,
    },
  });
  const dispatch = useDispatch();
  const { validation, setValidation } = useSubscriptionValidation(subscription);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const mealTypeList = useMealTypes();
  const planList = usePlans();
  const planDetail = usePlan(selectedPlanId);
  const [referencePlan, setReferencePlan] = useState<AdminPlanDetailResponse>();
  const zones = useZones();

  const [retriggerDiscountReq, setRetriggerDiscountReq] = useState<number>();
  const discounts = useDiscount(retriggerDiscountReq);
  const defaultDayList = DayList.map((i) => ({ name: i, enabled: true, prevented: false }));
  const [dayList, setDayList] = useState(defaultDayList);
  const [dateSelectionError, setDateSelectionError] = useState<DateValidationError | null>(null);
  const [selectedAdress, setSelectedAddress] = useState<IAddressResponse>();
  const [assignedDaysCache, setAssignedDaysCache] = useState();
  const assignedDaysDebounce = useDebounce(subscription.assignedDays, 1000);
  const [dayListValidation, setDayListValidation] = useState({ show: false, error: false, text: "" });

  useEffect(() => {
    setAssignedDaysCache(subscription.assignedDays);
  }, [assignedDaysDebounce]);

  const priceOffer = usePriceOffer(
    selectedAdress?.zone,
    subscription.planOptionId,
    assignedDaysCache,
    subscription.discountCode
  );

  const errorMessage = useMemo(() => {
    if (typeof dateSelectionError === "string") {
      switch (dateSelectionError) {
        case "shouldDisableDate": {
          return "Not an allowed day to start plan";
        }
        case "invalidDate": {
          return "Your starting date is not valid";
        }
        default: {
          return "Subscription start date should be one of the picked days";
        }
      }
    }
    return "";
  }, [dateSelectionError]);

  const handleFieldChange = (field: any, value?: any) => {
    setFormDirty(true);
    setSubscriptionEdit((state: any) => ({ ...state, [field]: value }));
  };

  const handleSelectedAddress = (value: string) => {
    const found = props.addressList?.find((i) => i.id === value);
    setSelectedAddress(found);
  };

  const handleDayListChange = (item: string, value: boolean) => {
    setDayList((state) => {
      let temp = [...state];
      const find = temp.find((i) => i.name === item);
      find!.enabled = value;
      return temp;
    });
  };

  useEffect(() => {
    setDayListValidation((state) => {
      return {
        ...state,
        error: dayList.filter((i: any) => i.enabled).length < Number(planDetail?.minDeliveryDaysOfWeek),
        text: `Minimum delivery days must be at least ${planDetail?.minDeliveryDaysOfWeek}`,
      };
    });
  }, [dayList]);

  // REMOVED - start day check on plan start date is removed
  // useEffect(() => {
  //   if (subscription.deliveryDays?.length && subscription.startDate) {
  //     const startingDay = moment(subscription.startDate).format("dddd").toLocaleUpperCase();
  //     if (subscription.deliveryDays?.includes(startingDay)) {
  //       setDateSelectionError((state) => {
  //         if (state === "minDate") {
  //           return null;
  //         } else {
  //           return state;
  //         }
  //       });
  //     } else {
  //       setDateSelectionError("minDate");
  //     }
  //   }
  // }, [subscription.deliveryDays, subscription.startDate]);

  // console.log("daylist", dayList);
  // console.log("subscription", subscription);
  // console.log("planList", planList);
  // console.log("planDetail", planDetail);
  // console.log("validation", validation);
  // console.log("daylist validation", dayListValidation);

  const handleDietaryTargetChange = (field: any, value?: any) => {
    setFormDirty(true);
    setSubscriptionEdit((state: any) => ({ ...state, dietaryTargets: { ...state.dietaryTargets, [field]: value } }));
  };

  const planChange = (event: SelectChangeEvent) => {
    const planId = event.target.value;

    setSelectedPlanId(planId);

    // Reset planOptionId when plan changes
    setSubscriptionEdit((state: any) => ({
      ...state,
      planId: planId,
      planOptionId: ""
    }));
    setFormDirty(true);
  };

  const planOptionChange = (event: SelectChangeEvent) => {
    const planOptionId = event.target.value;

    handleFieldChange("planOptionId", planOptionId);
  };

  const saveChanges = async () => {
    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    if (dateSelectionError) {
      return;
    }

    if (dayListValidation.error) {
      setDayListValidation((state) => ({ ...state, show: true }));
      return;
    }

    try {
      setLoading(true);
      let message;
      if (props.create) {
        let subscriptionRequestDto: CreateSubscriptionRequest = {
          ...subscription,
          deliveryDays: dayList.filter((i: any) => i.enabled).map((i: any) => i.name),
          discountCode: checkEmpty(subscription.discountCode) ? null : subscription.discountCode,
          startDate: subscription.startDate.format(defaultFormat),
        };
        message = "User is added to the plan successfully";
        await Api.Subscription.createSubscription(subscriptionRequestDto);
        resetForm();
      } else if (props.edit) {
        let updateSubscriptionRequestDto: UpdateSubscriptionRequest = {
          startDate: subscription.startDate.format(defaultFormat),
          assignedDays: subscription.assignedDays,
          dietaryTargets: {
            calories: Number(subscription.dietaryTargets.calories),
            carb: Number(subscription.dietaryTargets.carb),
            fat: Number(subscription.dietaryTargets.fat),
            protein: Number(subscription.dietaryTargets.protein),
          },
          deliveryDays: dayList.filter((i: any) => i.enabled).map((i: any) => i.name),
        };
        message = "Subscription has been updated successfully";
        await Api.Subscription.updateSubscription(subscription.id, updateSubscriptionRequestDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSubscriptionEdit({
      userId: props.userId,
      planId: "",
      planOptionId: "",
      discountCode: "",
      startDate: moment(lockedStart),
      assignedDays: "",
      targetCalories: "",
      deliveryDays: [],
    });
    setSelectedPlanId("");
    setValidation((state) => ({ ...state, show: false }));
    setDayListValidation((state) => ({ ...state, show: false }));
    setSelectedAddress(undefined);
  };

  const handlePanelClose = (...args: any) => {
    props.create && resetForm();
    props.onClose(args);
  };

  const shouldDisableDateCheck = (day: Moment) => {
    if (props.plan?.status === "ACTIVE") return false;
    return moment(day).startOf("day").isBefore(moment(lockedStart).startOf("day"));
  };

  // console.log("props", props);
  // console.log("props.plan", props.plan);
  // console.log("subscription", subscription);
  // console.log("validation", validation);
  // console.log("dateSelectionError", dateSelectionError);

  useEffect(() => {
    if (checkIf(lockedStart)) {
      handleFieldChange("startDate", moment(lockedStart));
    }
  }, [lockedStart]);

  useEffect(() => {
    if (props) {
      if (props.plan?.planOption?.plan?.id) {
        setSelectedPlanId(String(props.plan?.planOption?.plan?.id));
      }
      setSubscriptionEdit((state: any) => {
        return {
          ...state,
          id: props.plan?.id,
          planId: props.plan?.planOption?.plan?.id,
          planOptionId: props.plan?.planOption?.id,
          discountCode: props.plan?.discountCode,
          userId: props.userId,
          startDate: props.plan?.startDate ? moment(props.plan?.startDate) : moment(lockedStart),
          assignedDays: props.plan?.assignedDays,
          deliveryDays: props.plan?.deliveryDays,
          dietaryTargets: {
            calories: props.plan?.dietaryTargets?.calories,
            carb: props.plan?.dietaryTargets?.carb,
            fat: props.plan?.dietaryTargets?.fat,
            protein: props.plan?.dietaryTargets?.protein,
          },
        };
      });
    }
  }, [props]);

  useEffect(() => {
    setDayList(
      defaultDayList.map((li) => ({
        ...li,
        enabled: Boolean(props.plan?.deliveryDays?.includes(li.name as AdminPlanDetailResponseAvailableDaysEnum)),
        prevented: !Boolean(planDetail?.availableDays?.includes(li.name as AdminPlanDetailResponseAvailableDaysEnum)),
      }))
    );
  }, [selectedPlanId, planDetail]);

  // console.log("dateSelection error", dateSelectionError);
  // console.log("subscription", subscription);
  // console.log("day List", dayList);
  // console.log("plan detail available days", planDetail?.availableDays);

  useEffect(() => {
    setSubscriptionEdit((state: any) => ({
      ...state,
      deliveryDays: dayList.filter((i: any) => i.enabled).map((i: any) => i.name),
    }));
  }, [dayList]);

  useEffect(() => {
    if (planDetail) {
      const referenceDays = planDetail.availableDays;
      if (!props.plan) {
        setDayList(
          defaultDayList.map((li) => ({
            ...li,
            enabled: Boolean(referenceDays?.includes(li.name as AdminPlanDetailResponseAvailableDaysEnum)),
            prevented: !Boolean(referenceDays?.includes(li.name as AdminPlanDetailResponseAvailableDaysEnum)),
          }))
        );
      } else {
        setDayList((state) => {
          let temp = [...state];
          temp.forEach((item, key) => {
            if (!Boolean(referenceDays?.includes(item.name as AdminPlanDetailResponseAvailableDaysEnum))) {
              temp[key].prevented = true;
            }
            temp[key].enabled = Boolean(
              props.plan?.deliveryDays?.includes(item.name as AdminPlanDetailResponseAvailableDaysEnum)
            );
          });
          return temp;
        });
      }
    }
  }, [planDetail, props.plan]);

  const discountExplanation = useMemo(() => {
    let text = "";
    const status = priceOffer?.discount?.status;
    switch (status) {
      case PriceOfferDiscountResponseStatusEnum.NotApplicable:
        text = "Not applicable";
        break;
      case PriceOfferDiscountResponseStatusEnum.NotFound:
        text = "Not found";
        break;
      case PriceOfferDiscountResponseStatusEnum.Applicable:
        text = formatCurrency(priceOffer?.discount?.amount);
        break;
      default:
        break;
    }
    return text;
  }, [priceOffer]);

  // Discount Create
  const [createShow, setCreateShow] = useState(false);
  const handleClose = () => {
    setCreateShow(false);
  };
  const handleConfirm = () => {
    setRetriggerDiscountReq(generateRandom());
  };

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "562px",
            maxWidth: "562px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 16px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
          }}
        >
          {props.title}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "12px" }}>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Plan
              </Typography>
              <Select
                disabled={props.edit}
                sx={{ pointerEvents: `${props.edit ? "none" : "auto"}` }}
                value={subscription.planId || " "}
                onChange={planChange}
                error={validation.show && validation.planId?.error}
              >
                <MenuItem disabled value=" ">
                  <em>Select</em>
                </MenuItem>

                {planList?.map((planItem) => (
                  <MenuItem key={planItem.id} value={planItem.id}>
                    {planItem.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: "#FF4040" }} hidden={!(validation.show && validation.planId?.error)}>
                {validation.planId?.helperText}
              </FormHelperText>
            </Box>
            <Box className="form-section">
              <Typography component="span" className="form-label">
                Plan Option
              </Typography>
              <FormControl>
                <Select
                  disabled={props.edit}
                  sx={{ pointerEvents: `${props.edit ? "none" : "auto"}` }}
                  value={subscription.planOptionId || " "}
                  onChange={planOptionChange}
                  error={validation.show && validation.planOptionId?.error}
                >
                  <MenuItem disabled value=" ">
                    <em>Select</em>
                  </MenuItem>

                  {planDetail?.options?.map((planOptionItem) => (
                    <MenuItem key={planOptionItem.id} value={planOptionItem.id}>
                      <Box>
                        {planOptionItem.name?.en} - {planOptionItem.selection?.en}
                        <Typography sx={{ fontSize: 11, fontStyle: "italic" }}>
                          {Array.from(planOptionItem.mealTypeList as Set<PlanDetailPlanOptionResponseMealTypeListEnum>)
                            ?.map((i: string) => mealTypeList.find((li) => li.name === i)?.description)
                            .join(", ")}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText sx={{ color: "#FF4040" }} hidden={!(validation.show && validation.planOptionId?.error)}>
                  {validation.planOptionId?.helperText}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: "12px", alignItems: "baseline" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Start Date
                </Typography>
                <DatePicker
                  sx={{
                    "&.Mui2-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ff4040 !important" },
                    "&.Mui2-error .MuiFormHelperText-root": { color: "#ff4040 !important" },
                  }}
                  disabled={props.plan?.status === "ACTIVE"}
                  value={subscription.startDate}
                  className={`${dateSelectionError ? "Mui2-error" : ""}`}
                  onChange={(date) => handleFieldChange("startDate", moment(date))}
                  onError={(newError) => setDateSelectionError(newError)}
                  shouldDisableDate={shouldDisableDateCheck}
                  format={displayFormat}
                  slotProps={{
                    textField: {
                      onClick: () => setOpen(true),
                      helperText: errorMessage,
                    },
                  }}
                />
                <FormHelperText sx={{ color: "#FF4040" }} hidden={!(validation.show && validation.startDate?.error)}>
                  {validation.startDate?.helperText}
                </FormHelperText>
              </Box>
              <Box className="form-section">
                <Box sx={{ display: "flex" }}>
                  <Typography component="span" className="form-label">
                    Assigned Days
                  </Typography>
                  <Typography component="span" className="required-label">
                    *
                  </Typography>
                </Box>
                <TextField
                  className="form-field"
                  value={subscription?.assignedDays}
                  error={validation.show && validation.assignedDays?.error}
                  helperText={
                    validation.show && validation.assignedDays?.error ? validation.assignedDays.helperText : undefined
                  }
                  onChange={(e) => handleFieldChange("assignedDays", e.target.value)}
                />
              </Box>
            </Box>

            <Box className="form-section">
              <Box sx={{ display: "flex" }}>
                <Typography component="span" className="form-label">
                  Day Selection
                </Typography>
                <Typography component="span" className="required-label">
                  *
                </Typography>
              </Box>

              <FormControl sx={{ width: "100%" }}>
                <FormGroup sx={{ display: "flex", flexDirection: "row", columnGap: "4px" }}>
                  {dayList?.map((item) => (
                    <FormControlLabel
                      key={item.name}
                      className={item.enabled ? "selected" : ""}
                      disabled={item.prevented}
                      control={
                        <Checkbox
                          checked={item.enabled}
                          onChange={(e) => handleDayListChange(item.name, e.target.checked)}
                          name={item.name}
                        />
                      }
                      label={item.name.toLowerCase()}
                      sx={{
                        "&": {
                          margin: 0,
                          padding: "12px",
                          columnGap: "4px",
                          borderRadius: "8px",
                          border: "1px solid",
                          borderColor: "text.tableColor",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          width: "70px",
                        },
                        "&.selected": { background: "#E7F7ED", borderColor: "#E7F7ED" },
                        "& > .MuiButtonBase-root": { padding: 0 },
                        "& > .MuiTypography-root": {
                          fontSize: "10px",
                          fontWeight: 500,
                          lineHeight: "20px",
                          color: "text.dark2",
                          textTransform: "capitalize",
                        },
                        "&.selected > .MuiTypography-root": { color: "primary.main" },
                        fontSize: "18px",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
              {dayListValidation.show && dayListValidation.error ? (
                <FormHelperText sx={{ color: "text.error", margin: 0 }}>{dayListValidation.text}</FormHelperText>
              ) : null}
            </Box>

            <Box sx={{ display: "flex", gap: "12px" }}>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Target Calories
                </Typography>
                <TextField
                  className="form-field"
                  value={subscription?.dietaryTargets?.calories}
                  error={validation.show && validation?.calories?.error}
                  helperText={
                    validation.show && validation.calories?.error ? validation.calories?.helperText : undefined
                  }
                  onChange={(e) => handleDietaryTargetChange("calories", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cal</InputAdornment>,
                  }}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Protein
                </Typography>
                <TextField
                  className="form-field"
                  value={subscription?.dietaryTargets?.protein}
                  error={validation.show && validation?.protein?.error}
                  helperText={
                    validation.show && validation?.protein?.error ? validation.protein?.helperText : undefined
                  }
                  onChange={(e) => handleDietaryTargetChange("protein", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                  }}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Carbs
                </Typography>
                <TextField
                  className="form-field"
                  value={subscription?.dietaryTargets?.carb}
                  error={validation.show && validation?.carb?.error}
                  helperText={validation.show && validation?.carb?.error ? validation.carb?.helperText : undefined}
                  onChange={(e) => handleDietaryTargetChange("carb", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                  }}
                />
              </Box>
              <Box className="form-section">
                <Typography component="span" className="form-label">
                  Fat
                </Typography>
                <TextField
                  className="form-field"
                  value={subscription?.dietaryTargets?.fat}
                  error={validation.show && validation?.fat?.error}
                  helperText={validation.show && validation?.fat?.error ? validation.fat?.helperText : undefined}
                  onChange={(e) => handleDietaryTargetChange("fat", e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">gr</InputAdornment>,
                  }}
                />
              </Box>
            </Box>

            {props.create ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    borderBottomWidth: "1px",
                    borderColor: "#ececed",
                    borderBottomStyle: "solid",
                    padding: "8px 0",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Payment</Typography>
                </Box>

                <Box sx={{ display: "flex", gap: "12px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flex: "1 1 50%",
                      width: "calc(50% - 4px)",
                      flexDirection: "column",
                      gap: "0px",
                    }}
                  >
                    <Box className="form-section">
                      <Typography component="span" className="form-label">
                        Address
                      </Typography>
                      <Select
                        disabled={props.edit}
                        sx={{ pointerEvents: `${props.edit ? "none" : "auto"}` }}
                        value={selectedAdress?.id || " "}
                        onChange={(e) => handleSelectedAddress(e.target.value)}
                      >
                        <MenuItem value=" ">
                          <em>Select</em>
                        </MenuItem>

                        {props.addressList?.map((addressItem) => (
                          <MenuItem key={addressItem.id} value={addressItem.id}>
                            {addressItem.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box className="form-section">
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography component="span" className="form-label">
                          Discount
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            flex: 1,
                          }}
                        >
                          <FormControl sx={{ width: "100%" }}>
                            <Select
                              disabled={props.edit}
                              sx={{
                                pointerEvents: `${props.edit ? "none" : "auto"}`,
                                // margin: 0,
                                width: "100%",
                                maxWidth: "189px",
                              }}
                              value={subscription.discountCode || " "}
                              onChange={(e) => handleFieldChange("discountCode", e.target.value)}
                            >
                              <MenuItem value=" ">
                                <em>Select</em>
                              </MenuItem>

                              {discounts?.map((discountItem) => (
                                <MenuItem key={discountItem.id} value={discountItem.code}>
                                  {discountItem.code}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Tooltip placement="top" arrow title="Pressing the + button creates a quick discount">
                          <Button
                            color="success"
                            variant="contained"
                            onClick={() => setCreateShow(true)}
                            sx={{
                              borderRadius: "8px",
                              backgroundColor: "text.primary50",
                              fontSize: "12px",
                              fontWeight: 600,
                              lineHeight: "18px",
                              display: "flex",
                              width: "50px",
                              height: "50px",
                              minWidth: "auto",
                            }}
                          >
                            <Typography
                              sx={{
                                display: "flex",
                                color: "text.primary700",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <PlusSignIcon />
                            </Typography>
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flex: "1 1 50%",
                      width: "calc(50% - 4px)",
                      flexDirection: "column",
                      backgroundColor: "#F5F5F5",
                      padding: "12px",
                      borderRadius: "8px",
                      gap: "8px",
                      marginBottom: "10px",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>Price Details</Typography>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>Plan Price:</Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                        {formatCurrency(priceOffer?.price, "")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>Delivery Fee:</Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                        {formatCurrency(priceOffer?.deliveryFee, "")}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>Discount:</Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>{discountExplanation}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>Total Amount:</Typography>
                      <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                        {formatCurrency(priceOffer?.totalPrice, "")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            ) : null}

            {props.edit ? (
              <>
                <Box className="form-section">
                  <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>Price Details</Typography>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>Plan Price:</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                      {formatCurrency(props.plan?.payment?.price, "N/A")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>Delivery Fee:</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                      {formatCurrency(props.plan?.payment?.deliveryFee, "N/A")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 400 }}>
                      Discount{checkIf(props.plan?.payment?.discount) ? " (" + subscription.discountCode + ")" : ""}:
                    </Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>
                      {formatCurrency(props.plan?.payment?.discount, "")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>Total Amount:</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700 }}>
                      {formatCurrency(props.plan?.payment?.totalPrice, "N/A")}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : null}
          </Box>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <DiscountCrud
        create
        open={createShow}
        title="Create New Discount"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </Fragment>
  );
}
